import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { Component, ErrorHandler, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, signal } from '@angular/core'
import { disabled, form, required } from '@angular/forms/signals'

import { MatDialog } from '@angular/material/dialog'

import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom } from 'rxjs'
import { map, take, takeUntil, tap } from 'rxjs/operators'

import { MatSelectChange } from '@angular/material/select'
import { TuettuKieli } from '@lemon/core/model/common'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { Kirjanpitotili, PaatilikartanProfiili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { HierarkiaKirjanpitotili, TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialog, AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialogData } from './kirjanpitotili-muokkaa-paatilikartan-tilia.dialog'
import { AsiakkaanKirjanpitotiliMuokkaaDialog, AsiakkaanKirjanpitotiliMuokkaaDialogData } from './kirjanpitotili-muokkaa.dialog'

/** Flat node with expandable and level information */
export interface FlatKirjanpitotili {
  expandable: boolean
  name: string
  level: number
  kirjanpitotili: Kirjanpitotili
}

@Component({
  selector: '[asiakkaan-tilikartta-edit]',
  templateUrl: './asiakkaan-tilikartta-edit.component.html',
  styleUrls: ['./asiakkaan-tilikartta-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanTilikarttaEditComponent implements OnInit, OnDestroy {
  @Output() tilikarttaaMuokattiin: EventEmitter<boolean> = new EventEmitter<boolean>()

  readonly loading = signal(true)
  readonly commonError = signal('')
  flatatyt: Observable<FlatKirjanpitotili[]>

  valittuKieliSubject: BehaviorSubject<TuettuKieli> = new BehaviorSubject('fi')

  // Debug thingies
  asiakkaanTilikarttaUriObservable: Observable<string>
  asiakkaanTilikarttaEncodedUriObservable: Observable<string>
  paatilikarttaUriObservable: Observable<string>
  paatilikarttaEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  profiiliSubject: BehaviorSubject<PaatilikartanProfiili['avain']> = new BehaviorSubject(null)
  naytaProfiilivalintaObservable: Observable<boolean>

  private _hierarkiaObservable: Observable<HierarkiaKirjanpitotili[]>
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _profiilivalintaPoissa = signal(false)

  kieli = form(signal<TuettuKieli>('fi'), polku => {
    required(polku)
  })
  profiili = form(signal<PaatilikartanProfiili['avain'] | null>(null), polku => {
    required(polku)
    disabled(polku, () => this._profiilivalintaPoissa())
  })

  constructor(
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _tilikarttaService: TilikarttaService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
  ) {}

  ngOnInit(): void {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.asiakkaanTilikarttaUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._kirjanpitoUriService.annaAsiakkaanTilikartanUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.asiakkaanTilikarttaEncodedUriObservable = this.asiakkaanTilikarttaUriObservable.pipe(map(uri => this._debugService.createFirestoreLink(uri)))
    this.paatilikarttaUriObservable = this.kirjanpitajaOnDevaajaObservable.pipe(
      map(devaaja => {
        return 'admin/paatilikartta'
      }),
    )
    this.paatilikarttaEncodedUriObservable = this.paatilikarttaUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    this.naytaProfiilivalintaObservable = this._asiakasService.nykyisenAsiakkaanTilikaudetObservable.pipe(
      map(tilikaudet => {
        if (!tilikaudet || tilikaudet.length === 0) {
          return false
        }
        const profiilit = new Set(tilikaudet.map(t => t.tilikartanProfiili || 'maksuperusteinen'))
        return profiilit.size > 1
      }),
    )

    this._hierarkiaObservable = combineLatest([this._tilikarttaService.paatilikarttaObservable, this._tilikarttaService.nykyisenAsiakkaanTilikarttaObservable, this.profiiliSubject]).pipe(
      map(([paatilikartta, asiakkaanTilikartta, profiilinAvain]) => {
        if (!paatilikartta || !profiilinAvain) {
          return []
        }
        const tilit = this._tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, profiilinAvain)
        return this._tilikarttaJaettuService.muutaTililistausHierarkiaksi(tilit)
      }),
    )

    this.flatatyt = combineLatest([this._hierarkiaObservable, this.valittuKieliSubject]).pipe(
      map(([hierarkia, kieli]) => {
        const kaikki: FlatKirjanpitotili[] = []
        for (const juuri of hierarkia) {
          if (juuri.kirjanpitotili.aktiivinen || juuri.kirjanpitotili.piilotettuProfiilissa) {
            this.flattaa(juuri, 1, kaikki, kieli)
          }
        }
        return kaikki
      }),
      tap(() => {
        this.loading.set(false)
      }),
    )

    this._tilikarttaService.nykyisenAsiakkaanTilikartanProfiiliObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(profiili => {
      this.profiiliSubject.next(profiili?.avain || 'maksuperusteinen')
    })

    this.valittuKieliSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => asetaArvo(this.kieli, kieli))
    this.profiiliSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe(profiili => asetaArvo(this.profiili, profiili))
    this.naytaProfiilivalintaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nayta => this._profiilivalintaPoissa.set(nayta === false))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  kieliMuuttui(change: MatSelectChange) {
    this.valittuKieliSubject.next(change.value)
  }

  profiiliMuuttui(change: MatSelectChange<PaatilikartanProfiili['avain']>) {
    this.profiiliSubject.next(change.value)
  }

  private flattaa(hierarkia: HierarkiaKirjanpitotili, level: number, kaikki: FlatKirjanpitotili[], kieli: TuettuKieli) {
    const piilotettu = hierarkia.kirjanpitotili.piilotettuProfiilissa
    if (!piilotettu) {
      kaikki.push(this.makeFlat(hierarkia, level, kieli))
    }
    if (hierarkia.lapset) {
      const nextLevel = piilotettu ? level : level + 1
      for (const lapsi of hierarkia.lapset) {
        if (lapsi.kirjanpitotili.aktiivinen || lapsi.kirjanpitotili.piilotettuProfiilissa) {
          this.flattaa(lapsi, nextLevel, kaikki, kieli)
        }
      }
    }
  }

  private makeFlat(node: HierarkiaKirjanpitotili, level: number, kieli: TuettuKieli): FlatKirjanpitotili {
    const nimi = this._annaNimi(node, kieli)
    return {
      expandable: !!node.lapset && node.lapset.length > 0,
      name: node.kirjanpitotili.numero + ' ' + nimi,
      level: level,
      kirjanpitotili: node.kirjanpitotili,
    }
  }

  private _annaNimi(node: HierarkiaKirjanpitotili, kieli: TuettuKieli): string {
    if (node.kirjanpitotili.localisedName) {
      if (node.kirjanpitotili.localisedName[kieli]) {
        return node.kirjanpitotili.localisedName[kieli]
      }
    }
    return node.kirjanpitotili.nimi
  }

  async muokkaa(tili: Kirjanpitotili) {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
    const tilit = await firstValueFrom(this.flatatyt)

    if (asiakas) {
      if (tili.numero.length > 0 && tili.numero.length < 5) {
        const data: AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialogData = {
          asiakas: asiakas,
          lisaa: false,
          tili: tili,
          tilit: tilit,
        }
        this._dialog
          .open<AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialog, AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialogData, boolean>(AsiakkaanKirjanpitotiliMuokkaaPaatilikartanTiliaDialog, {
            data: data,
            autoFocus: false,
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe(tiliaMuokattiin => {
            if (tiliaMuokattiin) {
              this.tilikarttaaMuokattiin.emit(true)
            }
          })
      } else if (tili.numero.length === 5) {
        const data: AsiakkaanKirjanpitotiliMuokkaaDialogData = {
          asiakas: asiakas,
          lisaa: false,
          tili: tili,
          tilit: tilit,
        }
        this._dialog
          .open<AsiakkaanKirjanpitotiliMuokkaaDialog, AsiakkaanKirjanpitotiliMuokkaaDialogData, boolean>(AsiakkaanKirjanpitotiliMuokkaaDialog, { data: data, autoFocus: false })
          .afterClosed()
          .pipe(take(1))
          .subscribe(tiliaMuokattiin => {
            if (tiliaMuokattiin) {
              this.tilikarttaaMuokattiin.emit(true)
            }
          })
      }
    }
  }

  async lisaa() {
    const tilit = await firstValueFrom(this.flatatyt)

    this._asiakasService.nykyinenAsiakasObservable.pipe(take(1)).subscribe(asiakas => {
      if (asiakas) {
        const data: AsiakkaanKirjanpitotiliMuokkaaDialogData = {
          asiakas: asiakas,
          lisaa: true,
          tili: {
            aktiivinen: true,
            alvTyyppi: null,
            nimi: null,
            numero: null,
            oletusAlvKasittely: null,
            vanhempi: null,
            localisedName: {} as any,
          },
          tilit: tilit,
        }
        this._dialog
          .open<AsiakkaanKirjanpitotiliMuokkaaDialog, AsiakkaanKirjanpitotiliMuokkaaDialogData, boolean>(AsiakkaanKirjanpitotiliMuokkaaDialog, { data: data, autoFocus: false })
          .afterClosed()
          .pipe(take(1))
          .subscribe(tiliaMuokattiin => {
            if (tiliaMuokattiin) {
              this.tilikarttaaMuokattiin.emit(true)
            }
          })
      }
    })
  }

  drop(event: CdkDragDrop<FlatKirjanpitotili>) {
    if (event.currentIndex === event.previousIndex) {
      return
    }
    combineLatest([this.flatatyt, this._hierarkiaObservable, this._asiakasService.nykyinenAsiakasAvainObservable, this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable])
      .pipe(take(1))
      .subscribe(([flatatyt, hierarkia, asiakas, kirjanpitajanTiedot]) => {
        if (!flatatyt || !hierarkia || !asiakas?.avain || !kirjanpitajanTiedot?.uid) {
          return
        }

        // Hae flatatyista
        const raahattu = flatatyt[event.previousIndex]

        // Vain asiakaskohtaisia voi dragata
        if (raahattu.kirjanpitotili.numero.length !== 5) {
          return
        }

        const uusiIsanta = flatatyt[event.currentIndex]

        // Hae raahattu hierarkiasta
        const raahattuHierarkiassa = this._tilikarttaService.etsiTiliHierarkiasta(raahattu.kirjanpitotili.numero, hierarkia)
        if (!raahattuHierarkiassa) {
          return
        }

        // Tarkista, ettei uusi isäntä ole jokin raahatun lapsista
        const uusiIsantaRaahatunLapsissa: HierarkiaKirjanpitotili = this._tilikarttaService.etsiTiliHierarkiasta(uusiIsanta.kirjanpitotili.numero, [raahattuHierarkiassa])
        if (uusiIsantaRaahatunLapsissa) {
          return
        }

        // Hae uusi isäntä
        let uusiVanhempi = uusiIsanta.kirjanpitotili.numero.length === 4 ? uusiIsanta.kirjanpitotili.numero : null
        if (!uusiVanhempi && event.currentIndex > 0) {
          const edellinen = flatatyt[event.currentIndex - 1]
          uusiVanhempi = edellinen.kirjanpitotili.numero.length === 4 ? edellinen.kirjanpitotili.numero : null
        }
        if (!uusiVanhempi && event.currentIndex < flatatyt.length - 1) {
          const seuraava = flatatyt[event.currentIndex + 1]
          uusiVanhempi = seuraava.kirjanpitotili.numero.length === 4 ? seuraava.kirjanpitotili.numero : null
        }
        if (!uusiVanhempi) {
          const vanhempi = this._tilikarttaService.etsiTiliHierarkiasta(uusiIsanta.kirjanpitotili.vanhempi, hierarkia)
          uusiVanhempi = vanhempi.kirjanpitotili.numero.length === 4 ? vanhempi.kirjanpitotili.numero : null
        }

        if (!uusiVanhempi) {
          return
        }

        raahattu.kirjanpitotili.vanhempi = uusiVanhempi
        this._tilikarttaService.tallennaAsiakkaanTilikartanTili(asiakas, raahattu.kirjanpitotili, kirjanpitajanTiedot.uid).catch(error => {
          this.commonError.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.')
          this._errorHandler.handleError(error)
        })
      })
  }
}
